import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BodyOf, ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientId, PatientServicePlanId, PlanOfCareItemId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;
type TaskParams = BodyOf<"put", "./service_plans/:patientServicePlanId/tasks">["tasks"][number];
type TaskSection = TaskParams["section"];
type PlanOfCareItem = ResponseOf<
  "get",
  "./patients/:patientId/plan_of_care_items"
>["items"][number];

const SECTIONS: { id: TaskSection; label: string }[] = [
  { id: "HOMEMAKER", label: "Homemaker" },
  { id: "PERSONAL_CARE", label: "Personal care" },
  { id: "HMA", label: "Health maintenance activities" },
];

/** Editable row — a task the user is still working on, before it is saved. */
interface DraftTask {
  key: string;
  planOfCareItemId: PlanOfCareItemId | null;
  section: TaskSection;
  taskName: string;
  minutesPerTask: string;
  timesPerWeek: string;
  ame: boolean;
  justification: string;
  aiCertainty: number | null;
}

function toDraft(task: ServicePlan["tasks"][number], index: number): DraftTask {
  return {
    key: `task-${task.id}-${index}`,
    planOfCareItemId: task.planOfCareItemId,
    section: task.section,
    taskName: task.taskName,
    minutesPerTask: task.minutesPerTask === null ? "" : String(task.minutesPerTask),
    timesPerWeek: task.timesPerWeek === null ? "" : String(task.timesPerWeek),
    ame: task.ame,
    justification: task.justification ?? "",
    aiCertainty: task.aiCertainty,
  };
}

function parseNumber(value: string): number | null {
  if (value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function minutesPerWeek(task: DraftTask): number {
  return (parseNumber(task.minutesPerTask) ?? 0) * (parseNumber(task.timesPerWeek) ?? 0);
}

function formatHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

interface Props {
  isReadOnly: boolean;
  patientId: PatientId;
  plan: ServicePlan;
  servicePlanId: PatientServicePlanId;
}

export default function ServicePlanTasksStep({
  isReadOnly,
  patientId,
  plan,
  servicePlanId,
}: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [tasks, setTasks] = useState<DraftTask[]>(() => plan.tasks.map(toDraft));
  const [lrpHours, setLrpHours] = useState(() =>
    plan.lrpHoursPerWeek === null ? "" : String(plan.lrpHoursPerWeek)
  );
  const [nextRowId, setNextRowId] = useState(0);

  // Re-seed when the plan is refetched (another save, or a re-run import).
  useEffect(() => {
    setTasks(plan.tasks.map(toDraft));
    setLrpHours(plan.lrpHoursPerWeek === null ? "" : String(plan.lrpHoursPerWeek));
  }, [plan]);

  const planOfCareItems = useQuery(queries.servicePlan.planOfCareItems(patientId));

  const replaceTasksMutation = useMutation(mutations.servicePlan.replaceTasks(servicePlanId));
  const updateHeaderMutation = useMutation(mutations.servicePlan.updateHeader(servicePlanId));

  const unmappedCount = tasks.filter((task) => task.planOfCareItemId === null).length;
  const grandTotalMinutes = tasks.reduce((total, task) => total + minutesPerWeek(task), 0);

  const updateTask = (key: string, changes: Partial<DraftTask>) => {
    setTasks((current) =>
      current.map((task) => (task.key === key ? { ...task, ...changes } : task))
    );
  };

  const removeTask = (key: string) => {
    setTasks((current) => current.filter((task) => task.key !== key));
  };

  const addTask = (section: TaskSection) => {
    setTasks((current) => [
      ...current,
      {
        key: `new-${nextRowId}`,
        planOfCareItemId: null,
        section,
        taskName: "",
        minutesPerTask: "",
        timesPerWeek: "",
        ame: false,
        justification: "",
        aiCertainty: null,
      },
    ]);
    setNextRowId((id) => id + 1);
  };

  const handleSave = async () => {
    // orderInSection is reassigned from the on-screen order within each section.
    const orderBySection = new Map<TaskSection, number>();
    const payload: TaskParams[] = tasks.map((task) => {
      const order = orderBySection.get(task.section) ?? 0;
      orderBySection.set(task.section, order + 1);

      return {
        planOfCareItemId: task.planOfCareItemId,
        section: task.section,
        taskName: task.taskName,
        minutesPerTask: parseNumber(task.minutesPerTask),
        timesPerWeek: parseNumber(task.timesPerWeek),
        ame: task.ame,
        justification: task.justification === "" ? null : task.justification,
        aiCertainty: task.aiCertainty,
        orderInSection: order,
      };
    });

    try {
      await replaceTasksMutation.mutateAsync({ tasks: payload });

      // LRP hours belong to the header but are shown with these totals.
      const lrpValue = parseNumber(lrpHours);
      if (lrpValue !== plan.lrpHoursPerWeek) {
        await updateHeaderMutation.mutateAsync({
          startDate: plan.startDate,
          endDate: plan.endDate,
          program: plan.program,
          service: plan.service,
          caseManagerName: plan.caseManagerName,
          planStart: plan.planStart,
          visitDate: plan.visitDate,
          visitSummary: plan.visitSummary,
          lrpHoursPerWeek: lrpValue,
        });
      }

      queryClient.invalidateQueries({ queryKey: queries.servicePlan.byId(servicePlanId).queryKey });
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.list(patientId).queryKey });
      toast({
        title: "Tasks saved",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    } catch (error: unknown) {
      toast({
        title: "Could not save tasks",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    }
  };

  if (planOfCareItems.status === "error") {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Could not load plan of care items</AlertTitle>
        <AlertDescription>
          Tasks cannot be mapped until the patient&apos;s office has a plan of care type.
        </AlertDescription>
      </Alert>
    );
  }

  const isSaving = replaceTasksMutation.isPending || updateHeaderMutation.isPending;

  return (
    <VStack align="stretch" spacing={5}>
      {unmappedCount > 0 && (
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            {unmappedCount === 1
              ? "1 task still needs a plan of care item."
              : `${unmappedCount} tasks still need a plan of care item.`}
          </AlertDescription>
        </Alert>
      )}

      {SECTIONS.map((section) => {
        const sectionTasks = tasks.filter((task) => task.section === section.id);
        const sectionMinutes = sectionTasks.reduce(
          (total, task) => total + minutesPerWeek(task),
          0
        );

        return (
          <Box key={section.id} borderRadius="md" borderWidth="1px" padding={4}>
            <HStack marginBottom={3}>
              <Text fontSize="md" fontWeight="semibold">
                {section.label}
              </Text>
              <Spacer />
              <Text color="gray.600" fontSize="sm">
                {formatHours(sectionMinutes)} hrs/week
              </Text>
            </HStack>

            <VStack align="stretch" spacing={3}>
              {sectionTasks.length === 0 && (
                <Text color="gray.500" fontSize="sm">
                  No tasks in this section.
                </Text>
              )}

              {sectionTasks.map((task) => (
                <TaskRow
                  key={task.key}
                  isReadOnly={isReadOnly}
                  planOfCareItems={planOfCareItems.data ?? []}
                  task={task}
                  onChange={(changes) => updateTask(task.key, changes)}
                  onRemove={() => removeTask(task.key)}
                />
              ))}

              {!isReadOnly && (
                <Button
                  alignSelf="flex-start"
                  leftIcon={<AddIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => addTask(section.id)}
                >
                  Add task
                </Button>
              )}
            </VStack>
          </Box>
        );
      })}

      <HStack align="flex-end" spacing={6}>
        <Box maxWidth="200px">
          <FormLabel fontSize="sm">LRP hours per week</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            min={0}
            step="0.25"
            type="number"
            value={lrpHours}
            onChange={(event) => setLrpHours(event.target.value)}
          />
        </Box>
        <Spacer />
        <VStack align="flex-end" spacing={0}>
          <Text color="gray.600" fontSize="sm">
            Total direct care
          </Text>
          <Text fontSize="lg" fontWeight="semibold">
            {formatHours(grandTotalMinutes)} hrs/week
          </Text>
        </VStack>
      </HStack>

      {!isReadOnly && (
        // Sticky: a full calculator is 20+ rows, so Save must stay reachable
        // without scrolling to the bottom of the section list.
        <HStack
          backgroundColor="white"
          borderTopWidth="1px"
          bottom={0}
          justify="flex-end"
          paddingTop={3}
          position="sticky"
        >
          <Button colorScheme="blue" isLoading={isSaving} onClick={handleSave}>
            Save tasks
          </Button>
        </HStack>
      )}
    </VStack>
  );
}

function TaskRow(props: {
  isReadOnly: boolean;
  planOfCareItems: PlanOfCareItem[];
  task: DraftTask;
  onChange: (changes: Partial<DraftTask>) => void;
  onRemove: () => void;
}) {
  const { isReadOnly, planOfCareItems, task, onChange, onRemove } = props;
  const isUnmapped = task.planOfCareItemId === null;

  const itemsBySection = new Map<string, PlanOfCareItem[]>();
  for (const item of planOfCareItems) {
    itemsBySection.set(item.section, [...(itemsBySection.get(item.section) ?? []), item]);
  }

  return (
    <Box
      backgroundColor="gray.50"
      borderLeftColor={isUnmapped ? "orange.400" : "green.400"}
      borderLeftWidth="4px"
      borderRadius="md"
      padding={3}
    >
      <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={3}>
        <Box gridColumn={{ lg: "span 2" }}>
          <HStack marginBottom={1} spacing={2}>
            <FormLabel fontSize="sm" margin={0}>
              Plan of care item
            </FormLabel>
            {isUnmapped && <Badge colorScheme="orange">Needs mapping</Badge>}
          </HStack>
          <Select
            isDisabled={isReadOnly}
            placeholder="— not mapped —"
            value={task.planOfCareItemId ?? ""}
            onChange={(event) =>
              onChange({
                planOfCareItemId:
                  event.target.value === ""
                    ? null
                    : (Number(event.target.value) as PlanOfCareItemId),
              })
            }
          >
            {[...itemsBySection.entries()].map(([sectionName, items]) => (
              <optgroup key={sectionName} label={sectionName}>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} — {item.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <FormLabel fontSize="sm" marginBottom={0} marginTop={2}>
            Task name on the calculator
          </FormLabel>
          <Input
            isReadOnly={isReadOnly}
            placeholder="Task name from the calculator"
            size="sm"
            value={task.taskName}
            onChange={(event) => onChange({ taskName: event.target.value })}
          />
        </Box>

        <Box>
          <FormLabel fontSize="sm">Min / task</FormLabel>
          <Input
            isReadOnly={isReadOnly}
            min={0}
            type="number"
            value={task.minutesPerTask}
            onChange={(event) => onChange({ minutesPerTask: event.target.value })}
          />
          <FormLabel fontSize="sm" marginTop={2}>
            Times / week
          </FormLabel>
          <Input
            isReadOnly={isReadOnly}
            min={0}
            type="number"
            value={task.timesPerWeek}
            onChange={(event) => onChange({ timesPerWeek: event.target.value })}
          />
        </Box>

        <VStack align="stretch" spacing={2}>
          <Box>
            <Text color="gray.600" fontSize="sm">
              Min / week
            </Text>
            <Text fontWeight="semibold">{minutesPerWeek(task)}</Text>
          </Box>
          <Checkbox
            isChecked={task.ame}
            isDisabled={isReadOnly}
            onChange={(event) => onChange({ ame: event.target.checked })}
          >
            AME
          </Checkbox>
          {!isReadOnly && (
            <IconButton
              alignSelf="flex-start"
              aria-label="Remove task"
              colorScheme="red"
              icon={<DeleteIcon />}
              size="sm"
              variant="ghost"
              onClick={onRemove}
            />
          )}
        </VStack>
      </SimpleGrid>

      <Box marginTop={2}>
        <FormLabel fontSize="sm">Justification</FormLabel>
        <Textarea
          isReadOnly={isReadOnly}
          rows={2}
          value={task.justification}
          onChange={(event) => onChange({ justification: event.target.value })}
        />
      </Box>
    </Box>
  );
}
